import React, { useState, useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  startOfDay,
  addHours,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, CalendarOff } from 'lucide-react';
import { Appointment, Service, Staff, Tenant, TimeOff } from '../api';
import { toSalonTime, salonNow } from '../lib/datetime';
import { formatTimeOffSpan, timeOffSubject } from '../lib/timeOff';

/**
 * Event-block styling per status. The three text roles are a deliberate
 * hierarchy — name loudest, then service, then time. That used to be encoded
 * as three shades of the status hue (amber-900/700/500); now the status hue
 * carries the name only and the neutral ramp does the stepping down, which
 * keeps the hierarchy legible in both themes without needing a light and dark
 * shade for every status.
 */
function getStatusClasses(status: Appointment['status']) {
  switch (status) {
    case 'pending':
      return {
        monthBlock: 'border-warn/30 bg-warn-soft text-warn',
        borderColor: 'border-warn/60',
        bgColor: 'bg-warn-soft',
        textName: 'text-warn',
        textService: 'text-fg-secondary',
        textTime: 'text-fg-muted',
      };
    case 'confirmed':
      return {
        monthBlock: 'border-accent/30 bg-accent-soft text-accent',
        borderColor: 'border-brand-600',
        bgColor: 'bg-accent-soft',
        textName: 'text-accent',
        textService: 'text-fg-secondary',
        textTime: 'text-fg-muted',
      };
    case 'completed':
      return {
        monthBlock: 'border-success/30 bg-success-soft text-success',
        borderColor: 'border-success/60',
        bgColor: 'bg-success-soft',
        textName: 'text-success',
        textService: 'text-fg-secondary',
        textTime: 'text-fg-muted',
      };
    case 'cancelled':
    default:
      return {
        monthBlock: 'border-default bg-canvas text-fg-subtle line-through',
        borderColor: 'border-strong',
        bgColor: 'bg-muted',
        textName: 'text-fg-subtle line-through',
        textService: 'text-fg-subtle',
        textTime: 'text-fg-subtle',
      };
  }
}

interface LayoutInfo {
  column: number;
  totalColumns: number;
}

/**
 * Compute side-by-side column layout for overlapping appointments.
 * Returns a map of appointment id → { column, totalColumns }.
 */
function computeOverlapLayout(
  dayAppts: Appointment[],
  servicesList: Service[],
): Map<number, LayoutInfo> {
  const result = new Map<number, LayoutInfo>();
  if (dayAppts.length === 0) return result;

  // Build intervals sorted by start time
  const intervals = dayAppts
    .map((app) => {
      const start = toSalonTime(app.startTime);
      const duration =
        app.serviceDuration || servicesList.find((s) => s.id === app.serviceId)?.duration || 60;
      const end = new Date(start.getTime() + duration * 60000);
      return { id: app.id, start, end };
    })
    .sort((a, b) => a.start.getTime() - b.start.getTime() || a.end.getTime() - b.end.getTime());

  // Group into overlap clusters
  const clusters: (typeof intervals)[] = [];
  let currentCluster = [intervals[0]];
  let clusterEnd = intervals[0].end.getTime();

  for (let i = 1; i < intervals.length; i++) {
    const iv = intervals[i];
    if (iv.start.getTime() < clusterEnd) {
      // Overlaps with current cluster
      currentCluster.push(iv);
      clusterEnd = Math.max(clusterEnd, iv.end.getTime());
    } else {
      clusters.push(currentCluster);
      currentCluster = [iv];
      clusterEnd = iv.end.getTime();
    }
  }
  clusters.push(currentCluster);

  // Assign columns within each cluster
  for (const cluster of clusters) {
    const columns: number[][] = []; // columns[col] = list of end times
    for (const iv of cluster) {
      let placed = false;
      for (let col = 0; col < columns.length; col++) {
        // Check if this column is free (last event in column ended before this starts)
        const lastEnd = columns[col][columns[col].length - 1];
        if (iv.start.getTime() >= lastEnd) {
          columns[col].push(iv.end.getTime());
          result.set(iv.id, { column: col, totalColumns: 0 });
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([iv.end.getTime()]);
        result.set(iv.id, { column: columns.length - 1, totalColumns: 0 });
      }
    }
    // Set totalColumns for all in this cluster
    const total = columns.length;
    for (const iv of cluster) {
      const info = result.get(iv.id)!;
      info.totalColumns = total;
    }
  }

  return result;
}

interface AdminCalendarProps {
  tenant: Tenant;
  appointments: Appointment[];
  services: Service[];
  /** Active workers — drives the per-worker filter. Empty = salon-level salon. */
  staff?: Staff[];
  /**
   * Time off to explain in the banner, straight from `/admin/time-off/`.
   *
   * Deliberately NOT the `timeOff` on the public tenant detail: that list is
   * narrowed for customers — salon-wide, whole-day, and within 60 days
   * only — so the owner got no banner for the afternoon they had just
   * blocked off. That filter belongs on the customer's salon page, not on the
   * owner's own calendar, which shows every block including per-worker ones.
   */
  timeOff?: TimeOff[];
  /** Whether the viewer may create appointments (owners yes, workers no). */
  canAddAppointment?: boolean;
  onAddAppointment: (date: Date) => void;
  onAppointmentClick: (appointment: Appointment) => void;
}

type ViewType = 'month' | 'week' | 'day';

export default function AdminCalendar({
  tenant,
  appointments,
  services,
  staff = [],
  timeOff = [],
  canAddAppointment = true,
  onAddAppointment,
  onAppointmentClick,
}: AdminCalendarProps) {
  const [view, setView] = useState<ViewType>('month');
  const [currentDate, setCurrentDate] = useState(salonNow());
  // 'all' shows every worker; a number filters to one worker's appointments.
  const [staffFilter, setStaffFilter] = useState<number | 'all'>('all');

  const filteredAppointments = useMemo(
    () =>
      staffFilter === 'all' ? appointments : appointments.filter((a) => a.staffId === staffFilter),
    [appointments, staffFilter],
  );

  const handleSlotClick = (date: Date) => {
    if (canAddAppointment) onAddAppointment(date);
  };

  // Compute visible hour range from business hours with 2h buffer
  const { startHour, endHour } = useMemo(() => {
    const bh = tenant.businessHours;
    let earliest = 9;
    let latest = 18;
    if (bh) {
      const openHours: number[] = [];
      const closeHours: number[] = [];
      for (const val of Object.values(bh)) {
        if (val) {
          const [oh] = val.open.split(':').map(Number);
          const [ch, cm] = val.close.split(':').map(Number);
          openHours.push(oh);
          closeHours.push(cm > 0 ? ch + 1 : ch);
        }
      }
      if (openHours.length > 0) {
        earliest = Math.min(...openHours);
        latest = Math.max(...closeHours);
      }
    }
    return {
      startHour: Math.max(0, earliest - 2),
      endHour: Math.min(24, latest + 2),
    };
  }, [tenant.businessHours]);

  const visibleHours = useMemo(
    () => Array.from({ length: endHour - startHour }, (_, i) => startHour + i),
    [startHour, endHour],
  );

  const next = () => {
    if (view === 'month') setCurrentDate(addMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const prev = () => {
    if (view === 'month') setCurrentDate(subMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };

  const goToDay = (day: Date) => {
    setCurrentDate(day);
    setView('day');
  };

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate));
    const end = endOfWeek(endOfMonth(currentDate));
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate);
    const end = endOfWeek(currentDate);
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  /**
   * Time off overlapping whatever the calendar is currently showing.
   *
   * A banner, not an overlay on the grid: a real overlay needs per-day clipped
   * intervals positioned against the fixed 80px/hour rows in three views, and the
   * API already refuses a booking clicked into a block. This answers the question
   * the grid can't — "why is this week empty?".
   *
   * No date floor or ceiling: the rows arrive unfiltered, so paging back to last
   * month explains that week's gap too. The API returns them already sorted.
   */
  const visibleTimeOff = useMemo(() => {
    if (timeOff.length === 0) return [];
    const days = view === 'month' ? monthDays : view === 'week' ? weekDays : [currentDate];
    const from = format(days[0], 'yyyy-MM-dd');
    const to = format(days[days.length - 1], 'yyyy-MM-dd');
    return timeOff.filter((t) => t.startDate <= to && t.endDate >= from);
  }, [timeOff, view, monthDays, weekDays, currentDate]);

  const renderMonthView = () => {
    return (
      <div className="border-default grid grid-cols-7 border-t border-l">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="border-default bg-canvas text-fg-muted border-r border-b py-2 text-center text-xs font-semibold"
          >
            {day}
          </div>
        ))}
        {monthDays.map((day, i) => {
          const dayAppointments = filteredAppointments.filter((app) =>
            isSameDay(toSalonTime(app.startTime), day),
          );
          const isCurrentMonth = isSameMonth(day, currentDate);

          return (
            <div
              key={i}
              onClick={() => goToDay(day)}
              className={`border-default hover:bg-canvas relative min-h-[120px] cursor-pointer border-r border-b p-2 transition-colors ${
                !isCurrentMonth ? 'bg-canvas/50' : 'bg-surface'
              }`}
            >
              <div className="flex items-start">
                <span
                  className={`text-sm font-medium ${
                    isSameDay(day, salonNow())
                      ? 'bg-brand-600 flex h-6 w-6 items-center justify-center rounded-full text-white'
                      : isCurrentMonth
                        ? 'text-fg'
                        : 'text-fg-subtle'
                  }`}
                >
                  {format(day, 'd')}
                </span>
              </div>
              <div className="mt-2 space-y-1">
                {dayAppointments.slice(0, 3).map((app) => {
                  const colors = getStatusClasses(app.status);
                  return (
                    <div
                      key={app.id}
                      className={`cursor-pointer truncate rounded border p-1 text-[10px] ${colors.monthBlock}`}
                      title={`${format(toSalonTime(app.startTime), 'h:mm a')} - ${app.customer.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAppointmentClick(app);
                      }}
                    >
                      {format(toSalonTime(app.startTime), 'h:mm a')} {app.customer.name}
                    </div>
                  );
                })}
                {dayAppointments.length > 3 && (
                  <div className="text-fg-muted pl-1 text-[10px]">
                    + {dayAppointments.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    return (
      <div className="border-default flex flex-col overflow-x-auto border-t">
        <div className="border-default bg-canvas sticky top-0 z-10 flex border-b">
          <div className="border-default w-20 flex-shrink-0 border-r"></div>
          {weekDays.map((day) => (
            <div
              key={day.toISOString()}
              className="border-default min-w-[120px] flex-1 border-r py-3 text-center"
            >
              <div className="text-fg-muted text-xs font-semibold uppercase">
                {format(day, 'EEE')}
              </div>
              <div
                className={`mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-lg font-bold ${
                  isSameDay(day, salonNow()) ? 'bg-brand-600 text-white' : 'text-fg'
                }`}
              >
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>
        <div className="relative flex">
          <div className="border-default bg-canvas w-20 flex-shrink-0 border-r">
            {visibleHours.map((hour) => (
              <div
                key={hour}
                className="border-subtle text-fg-subtle h-20 border-b pt-2 pr-2 text-right text-[10px]"
              >
                {format(addHours(startOfDay(new Date()), hour), 'h a')}
              </div>
            ))}
          </div>
          <div className="relative flex flex-1">
            {weekDays.map((day) => (
              <div
                key={day.toISOString()}
                className="border-default relative min-w-[120px] flex-1 border-r"
              >
                {visibleHours.map((hour) => (
                  <div
                    key={hour}
                    className="group border-subtle relative h-20 border-b"
                    onClick={() => handleSlotClick(addHours(startOfDay(day), hour))}
                  >
                    {canAddAppointment && (
                      <div className="bg-canvas/30 absolute inset-0 flex cursor-pointer items-center justify-center opacity-0 group-hover:opacity-100">
                        <Plus className="text-fg-muted h-4 w-4" />
                      </div>
                    )}
                  </div>
                ))}
                {(() => {
                  const dayAppts = filteredAppointments.filter((app) =>
                    isSameDay(toSalonTime(app.startTime), day),
                  );
                  const layout = computeOverlapLayout(dayAppts, services);
                  return dayAppts.map((app) => {
                    const date = toSalonTime(app.startTime);
                    const hour = date.getHours();
                    const minutes = date.getMinutes();
                    const top = (hour + minutes / 60 - startHour) * 80;
                    const duration =
                      app.serviceDuration ||
                      services.find((s) => s.id === app.serviceId)?.duration ||
                      60;
                    const height = (duration / 60) * 80;
                    const colors = getStatusClasses(app.status);
                    const info = layout.get(app.id);
                    const col = info?.column ?? 0;
                    const total = info?.totalColumns ?? 1;
                    const widthPct = 100 / total;
                    const leftPct = col * widthPct;

                    if (top + height < 0 || top > visibleHours.length * 80) return null;

                    return (
                      <div
                        key={app.id}
                        className={`absolute z-0 cursor-pointer overflow-hidden border-l-4 p-1 shadow-sm ${colors.borderColor} ${colors.bgColor}`}
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          left: `calc(${leftPct}% + 1px)`,
                          width: `calc(${widthPct}% - 2px)`,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick(app);
                        }}
                      >
                        <div className={`truncate text-[10px] font-bold ${colors.textName}`}>
                          {app.customer.name}
                        </div>
                        <div className={`truncate text-[9px] ${colors.textService}`}>
                          {app.serviceName || 'Unknown Service'}
                          {staffFilter === 'all' && app.staffName ? ` · ${app.staffName}` : ''}
                        </div>
                        <div className={`text-[8px] ${colors.textTime}`}>
                          {format(date, 'h:mm a')}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const day = currentDate;
    return (
      <div className="border-default flex flex-col overflow-x-auto border-t">
        <div className="border-default bg-canvas sticky top-0 z-10 flex border-b">
          <div className="border-default w-20 flex-shrink-0 border-r"></div>
          <div className="border-default flex-1 border-r py-3 text-center">
            <div className="text-fg-muted text-xs font-semibold uppercase">
              {format(day, 'EEEE')}
            </div>
            <div
              className={`mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-lg font-bold ${
                isSameDay(day, salonNow()) ? 'bg-brand-600 text-white' : 'text-fg'
              }`}
            >
              {format(day, 'd')}
            </div>
          </div>
        </div>
        <div className="relative flex">
          <div className="border-default bg-canvas w-20 flex-shrink-0 border-r">
            {visibleHours.map((hour) => (
              <div
                key={hour}
                className="border-subtle text-fg-subtle h-20 border-b pt-2 pr-2 text-right text-[10px]"
              >
                {format(addHours(startOfDay(new Date()), hour), 'h a')}
              </div>
            ))}
          </div>
          <div className="relative flex flex-1">
            <div className="border-default relative flex-1 border-r">
              {visibleHours.map((hour) => (
                <div
                  key={hour}
                  className="group border-subtle relative h-20 border-b"
                  onClick={() => handleSlotClick(addHours(startOfDay(day), hour))}
                >
                  {canAddAppointment && (
                    <div className="bg-canvas/30 absolute inset-0 flex cursor-pointer items-center justify-center opacity-0 group-hover:opacity-100">
                      <Plus className="text-fg-muted h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}
              {(() => {
                const dayAppts = filteredAppointments.filter((app) =>
                  isSameDay(toSalonTime(app.startTime), day),
                );
                const layout = computeOverlapLayout(dayAppts, services);
                return dayAppts.map((app) => {
                  const date = toSalonTime(app.startTime);
                  const hour = date.getHours();
                  const minutes = date.getMinutes();
                  const top = (hour + minutes / 60 - startHour) * 80;
                  const duration =
                    app.serviceDuration ||
                    services.find((s) => s.id === app.serviceId)?.duration ||
                    60;
                  const height = (duration / 60) * 80;
                  const colors = getStatusClasses(app.status);
                  const info = layout.get(app.id);
                  const col = info?.column ?? 0;
                  const total = info?.totalColumns ?? 1;
                  const widthPct = 100 / total;
                  const leftPct = col * widthPct;

                  if (top + height < 0 || top > visibleHours.length * 80) return null;

                  return (
                    <div
                      key={app.id}
                      className={`absolute z-0 cursor-pointer overflow-hidden border-l-4 p-2 shadow-sm ${colors.borderColor} ${colors.bgColor}`}
                      style={{
                        top: `${top}px`,
                        height: `${height}px`,
                        left: `calc(${leftPct}% + 1px)`,
                        width: `calc(${widthPct}% - 2px)`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAppointmentClick(app);
                      }}
                    >
                      <div className={`truncate text-xs font-bold ${colors.textName}`}>
                        {app.customer.name}
                      </div>
                      <div className={`truncate text-xs ${colors.textService}`}>
                        {app.serviceName || 'Unknown Service'}
                        {staffFilter === 'all' && app.staffName ? ` · ${app.staffName}` : ''}
                      </div>
                      <div className={`text-xs ${colors.textTime}`}>{format(date, 'h:mm a')}</div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
      <div className="border-default flex flex-col items-center justify-between gap-4 border-b p-4 sm:flex-row">
        <div className="flex items-center gap-4">
          <h2 className="text-fg text-lg font-bold">
            {format(
              currentDate,
              view === 'month'
                ? 'MMMM yyyy'
                : view === 'week'
                  ? "'Week of' MMM d, yyyy"
                  : 'MMMM d, yyyy',
            )}
          </h2>
          <div className="bg-muted flex items-center rounded-lg p-1">
            <button
              onClick={prev}
              className="hover:bg-surface rounded-md p-1 transition-all hover:shadow-sm"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCurrentDate(salonNow())}
              className="hover:bg-surface rounded-md px-2 py-1 text-xs font-medium transition-all hover:shadow-sm"
            >
              Today
            </button>
            <button
              onClick={next}
              className="hover:bg-surface rounded-md p-1 transition-all hover:shadow-sm"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Wraps rather than overflows: the filter, the view switch and the add
            button together need more width than a phone has. */}
        <div className="flex w-full flex-wrap items-center justify-center gap-2 sm:w-auto sm:justify-end">
          {staff.length > 0 && (
            <select
              value={staffFilter === 'all' ? 'all' : String(staffFilter)}
              onChange={(e) =>
                setStaffFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))
              }
              className="focus:ring-brand-600 border-default bg-surface text-fg-secondary rounded-lg border px-3 py-1.5 text-xs font-medium outline-none focus:ring-2"
              title="Filter by worker"
            >
              <option value="all">All staff</option>
              {staff.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          )}
          <div className="bg-muted flex rounded-lg p-1">
            <button
              onClick={() => setView('month')}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-all ${
                view === 'month'
                  ? 'text-accent bg-surface shadow-sm'
                  : 'text-fg-muted hover:text-fg-secondary'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setView('week')}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-all ${
                view === 'week'
                  ? 'text-accent bg-surface shadow-sm'
                  : 'text-fg-muted hover:text-fg-secondary'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setView('day')}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-all ${
                view === 'day'
                  ? 'text-accent bg-surface shadow-sm'
                  : 'text-fg-muted hover:text-fg-secondary'
              }`}
            >
              Day
            </button>
          </div>
          {canAddAppointment && (
            <button
              onClick={() => onAddAppointment(salonNow())}
              className="bg-brand-600 hover:bg-brand-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Appointment
            </button>
          )}
        </div>
      </div>

      {visibleTimeOff.length > 0 && (
        <div className="border-warn/30 bg-warn-soft flex items-start gap-2.5 border-b px-4 py-3">
          <CalendarOff className="text-warn mt-0.5 h-4 w-4 shrink-0" />
          <div className="text-warn space-y-0.5 text-sm">
            {visibleTimeOff.map((t) => (
              // One line per block rather than a joined list of dates: the rows no
              // longer share a subject, so "Salon closed" can't be hoisted out in
              // front of a worker's afternoon off. `note` is the owner's own
              // private text ("surgery") and this component only ever renders
              // inside the admin dashboard, so a tooltip is where it belongs.
              <p key={t.id} title={t.note || undefined}>
                <span className="font-semibold">{timeOffSubject(t)}</span> {formatTimeOffSpan(t)}
              </p>
            ))}
            <p className="text-warn/80">New bookings are blocked for those times.</p>
          </div>
        </div>
      )}

      <div className="max-h-[700px] overflow-auto">
        {view === 'month'
          ? renderMonthView()
          : view === 'week'
            ? renderWeekView()
            : renderDayView()}
      </div>
    </div>
  );
}
